import {
  PTable,
  PTableHead,
  PTableHeadCell,
  PTableHeadRow,
  PTableBody,
  PTableRow,
  PTableCell,
  PText,
  PSpinner,
  PHeadline,
  PFlex,
  PPagination,
  PButtonPure,
  PFlexItem,
  PButtonGroup,
  PPopover
} from '@porsche-design-system/components-react'
import type { SortingChangeEvent } from '@porsche-design-system/components-react'
import React from 'react'

import { getReporting } from '../../../../utils/api/Apps'

import { ReportingContext } from '../../../../utils/Context/ReportingContext'
import { headlinesAllSingleRatings } from '../../../../utils/ratingService'
import Panel from '../../../Panel/Panel'
import StarRating from '../../../StarRating/StarRating'

import { PortalContext } from '../../../../utils/PortalContext'

import styles from './tablesingleratings.module.css'

type HeadSorting = {
  id: string
  name: string
  active?: boolean
  direction?: 'asc' | 'desc'
}

type DataSorting = {
  additionalInformation: any
  col0: string
  col1: number
  col2: string
  col3: any
}

type Props = {
  content: DataSorting[]
  loading: boolean
}

type Order = 'appName' | 'date' | 'rating' | 'comment' | 'sentiment'
type Sort = 'DESC' | 'ASC'

const TableSingleRatings: React.FC<Props> = ({ content, loading }) => {
  const headSorting: HeadSorting[] = headlinesAllSingleRatings.map<HeadSorting>((item, i) => ({
    id: 'col' + i,
    name: item,
    active: i === 4,
    direction: 'asc'
  }))

  const { filter, totalRatings } = React.useContext(ReportingContext)
  const { setOpenModalExport } = React.useContext(PortalContext)
  const [head, setHead] = React.useState(headSorting)
  const [data, setData] = React.useState(content)
  const [feedbacksPerPage, setFeedbacksPerPage] = React.useState(20)
  const [orderBy, setOrderBy] = React.useState<Order>('date')
  const [sortBy, setSortBy] = React.useState<Sort>('DESC')
  const [newDataLoading, setNewDataLoading] = React.useState(false)
  const [activePage, setActivePage] = React.useState(1)

  const onSortingChange = React.useCallback((e: CustomEvent<SortingChangeEvent>) => {
    const { id, direction } = e.detail as SortingChangeEvent & { id: keyof DataSorting; direction: String }
    setHead((prev) => prev.map((item) => ({ ...item, active: false, ...(item.id === id && e.detail) })))
    setNewDataLoading(true)
    let order: Order = 'date'
    setData([])
    switch (e.detail.id) {
      case 'col0':
        order = 'appName'
        break
      case 'col1':
        order = 'rating'
        break
      case 'col2':
        order = 'comment'
        break
      default:
        order = 'date'
        break
    }
    const sorter: any = direction.toUpperCase()
    getReporting(filter, 'bydetails', order, sorter, 0, feedbacksPerPage)
      .then((res) => parseResponse(res))
      .then((val) => {
        setSortBy(sorter)
        setOrderBy(order)
        setActivePage(1)
        if (val.length > 0 && val instanceof Array) {
          setData(createCellData(val))
        }
        setNewDataLoading(false)
      })
  }, [filter, feedbacksPerPage])

  const parseResponse = (res: any) => {
    return res
      .clone()
      .json()
      .catch(() => res.text())
  }

  const feedbacksPerPageClickHandler = React.useCallback((feedbacks: number) => {
    setNewDataLoading(true)
    setData([])
    setFeedbacksPerPage(feedbacks)
    getReporting(filter, 'bydetails', orderBy, sortBy, 0, feedbacks)
      .then((res) => parseResponse(res))
      .then((val) => {
        setActivePage(1)
        if (val.length > 0 && val instanceof Array) {
          setData(createCellData(val))
        }
        setNewDataLoading(false)
      })
  }, [filter, orderBy, sortBy])

  const pageChange = React.useCallback(
    (event) => {
      const page = event.detail.page
      setNewDataLoading(true)
      setData([])
      getReporting(filter, 'bydetails', orderBy, sortBy, (page - 1) * feedbacksPerPage, feedbacksPerPage)
        .then((res) => parseResponse(res))
        .then((val) => {
          if (val.length > 0 && val instanceof Array) {
            setActivePage(page)
            setData(createCellData(val))
          }
          setNewDataLoading(false)
        })
    },
    [filter, orderBy, sortBy, feedbacksPerPage]
  )

  const createCellData = (val: Array<any>) => {
    return val?.map((rating: any) => {
      return {
        additionalInformation: {
          subapp: rating.subappName,
          module: rating.moduleName
        },
        col0: rating.appName,
        col1: rating.rating,
        col2: rating.comment,
        col3: rating.date
      }
    })
  }

  const openExportDialog = () => {
    setOpenModalExport(true)
  }

  React.useEffect(() => {
    setData(content)
  }, [content])

  return (
    <>
      <PHeadline variant='headline-5'>Einzelbewertung</PHeadline>
      <Panel withMargin withPadding>
        <>
          <PFlex justifyContent='space-between' alignContent='center' className={styles.tableActions}>
            <PFlexItem>
              <PHeadline variant='headline-5'>
                Bewertungen ({data.length > 0 && feedbacksPerPage}
                {data.length === 0 && 0})
              </PHeadline>
            </PFlexItem>
            <PFlexItem>
              <PButtonGroup>
                <PPopover>
                  Über diese Aktionsleiste können Sie die maximal auf einmal angezeigten Bewertungen einrichten, sowie
                  einen aktuellen Export der ausgewählten Daten herunterladen.
                </PPopover>
                <PButtonPure size='small' onClick={() => feedbacksPerPageClickHandler(20)} icon='list'>
                  20
                </PButtonPure>
                <PButtonPure size='small' onClick={() => feedbacksPerPageClickHandler(50)} icon='list'>
                  50
                </PButtonPure>
                <PButtonPure size='small' onClick={() => feedbacksPerPageClickHandler(100)} icon='list'>
                  100
                </PButtonPure>
                <PText>|</PText>
                <PButtonPure size='small' onClick={openExportDialog} icon='save'>
                  Export
                </PButtonPure>
              </PButtonGroup>
            </PFlexItem>
          </PFlex>
          <PTable caption='Einzelbewertungen' onSortingChange={onSortingChange}>
            <PTableHead>
              <PTableHeadRow>
                {head.map((headline, i) => (
                  <PTableHeadCell key={i} sort={headline}>
                    {headline.name}
                  </PTableHeadCell>
                ))}
              </PTableHeadRow>
            </PTableHead>
            <PTableBody>
              {!loading &&
                data.length > 0 &&
                data.map((item, i) => (
                  <PTableRow key={i}>
                    <PTableCell multiline style={{ maxWidth: '6rem' }}>
                      {item.col0}
                      {item.additionalInformation.subapp && <span> / {item.additionalInformation.subapp} </span>}
                      {item.additionalInformation.module && <span> / {item.additionalInformation.module} </span>}
                    </PTableCell>
                    <PTableCell>
                      {item.col1} <StarRating rating={item.col1} />
                    </PTableCell>
                    <PTableCell multiline style={{ maxWidth: '26rem' }}>
                      <PText>{item.col2}</PText>
                    </PTableCell>
                    <PTableCell>
                      {new Date(item.col3).toLocaleDateString('de-de', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })}
                    </PTableCell>
                  </PTableRow>
                ))}
            </PTableBody>
          </PTable>
          {data.length > 0 && (
            <PFlex justifyContent='center' className={styles.buttonWrapper}>
              <PPagination
                totalItemsCount={totalRatings}
                itemsPerPage={feedbacksPerPage}
                activePage={activePage}
                onPageChange={pageChange}
              />
            </PFlex>
          )}
          {!loading && data.length === 0 && (
            <div className={styles.noData}>
              <PText align='center' className={styles.noDataText}>
                Keine Daten
              </PText>
            </div>
          )}
          {(loading || newDataLoading) && (
            <div className={styles.loaderBlocker}>
              <PText align='center' className={styles.loaderText}>
                Daten werden geladen...
              </PText>
              <PSpinner className={styles.loaderSpinner} />
            </div>
          )}
        </>
      </Panel>
    </>
  )
}

export default React.memo(TableSingleRatings)
